/**
 * Analytics — lightweight event tracking layer.
 *
 * Fires structured events to window.gtag (Google Analytics 4) when available.
 * Falls back to console.debug so events are always visible in dev tools
 * without crashing when GA is not loaded.
 *
 * To connect to GA4: add the standard gtag.js snippet to public/index.html
 * and set window.gtag = gtag. No other changes required here.
 *
 * Event shape: track(eventName, properties)
 *   eventName — snake_case, matches GA4 recommended event names where possible
 *   properties — flat object of string/number values
 */

function track(eventName, properties = {}) {
  try {
    if (typeof window !== "undefined") {
      // GA4 via gtag
      if (typeof window.gtag === "function") {
        window.gtag("event", eventName, properties);
      }
      // Dev visibility
      if (process.env.NODE_ENV === "development") {
        console.debug(`[analytics] ${eventName}`, properties);
      }
    }
  } catch (err) {
    // never let tracking break the UI — but log for debugging
    if (process.env.NODE_ENV === "development") {
      console.error("[analytics] tracking error", err);
    }
  }
}

// ─── Named event helpers ──────────────────────────────────────────────────────

/** Homepage: user clicks a category card or hero CTA */
export function trackCategoryClick(categoryName, destination) {
  track("category_click", { category: categoryName, destination });
}

/** Canvas: user opens a canvas product detail page */
export function trackCanvasProductOpen(productHandle, productTitle) {
  track("canvas_product_open", { handle: productHandle, title: productTitle });
}

/** Canvas: user changes size or frame selection */
export function trackCanvasConfigure(productHandle, size, frame, pricingSource) {
  track("canvas_configure", { handle: productHandle, size, frame, pricing_source: pricingSource });
}

/** Canvas: user clicks Complete Purchase on Store */
export function trackCanvasPurchaseCTA(productHandle, size, frame, price, variantId) {
  track("canvas_purchase_cta", {
    handle: productHandle,
    size,
    frame,
    price,
    variant_id: variantId || "none",
  });
}

/** Canvas: user clicks Request Custom Size */
export function trackCanvasCustomQuote(productHandle) {
  track("canvas_custom_quote_click", { handle: productHandle });
}

/** Murals: user opens a wall mural product detail page */
export function trackMuralProductOpen(productHandle, productTitle) {
  track("mural_product_open", { handle: productHandle, title: productTitle });
}

/** Any page: user clicks a Request Quote CTA */
export function trackQuoteCTAClick(sourcePage, projectType) {
  track("quote_cta_click", { source_page: sourcePage, project_type: projectType });
}

/** Request Quote page: user successfully submits the quote form */
export function trackQuoteSubmit(projectType) {
  track("quote_submit", { project_type: projectType });
}

/** Floating actions: user clicks WhatsApp */
export function trackWhatsAppClick(sourcePage) {
  track("whatsapp_click", { source_page: sourcePage });
}

/** Floating actions: user clicks Facebook */
export function trackFacebookClick(sourcePage) {
  track("facebook_click", { source_page: sourcePage });
}

/** Stickers / Banners / Vehicle: user clicks quote CTA from configurator */
export function trackConfiguratorQuoteClick(category, coverage, quantity) {
  track("configurator_quote_click", { category, coverage, quantity: String(quantity) });
}
